import { Box, Group, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useCreateFinancialItem } from 'src/financial/financial-data/financial-item/useCreateFinancialItem';
import { FinancialItems } from 'src/financial/financial-data/hooks/useFinancialItems';
import { SectionAddAction } from 'src/cashflows/components/SectionAddButton';
import { formatMoney } from 'src/common/currency';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { getCategoryEntries } from './addProductEntries';
import { FinancialSectionTable } from './FinancialSectionTable';
import { FinancialRow, buildCategories, extractRows, netWorthOf } from './financialRows';
import { useCurrency } from 'src/common/hooks/useCurrency';

type Props = {
  clientId: string;
  clientFirstName: string;
  financialItems: FinancialItems;
};

export function FinancialContentRework({ clientId, clientFirstName, financialItems }: Props) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const { flagEnabledFinancialItemOwners } = useFlags();
  const { beginCreate, modals } = useCreateFinancialItem({ clientId });

  const categories = buildCategories(
    extractRows(
      financialItems,
      clientFirstName,
      flagEnabledFinancialItemOwners ? { clientId } : undefined,
    ),
  );

  const netWorth = netWorthOf(categories);

  const balanceSheetCategories = categories.filter(c => c.key !== 'protections');
  const protectionCategory = categories.find(c => c.key === 'protections');

  const handleRowClick = (row: FinancialRow) => {
    navigate(`/client/${clientId}/financial-items/${row.navProduct}/${row.id}`);
  };

  const addActionForCategory = (key: string): SectionAddAction | undefined => {
    switch (key) {
      case 'spendingAccounts':
        return { onAdd: () => beginCreate('SPENDING_ACCOUNT', 'SpendingAccount') };
      case 'protections':
        return { onAdd: () => beginCreate('PROTECTION', 'Protection') };
      case 'assets':
        return { entries: getCategoryEntries('ASSET'), onSelect: p => beginCreate('ASSET', p) };
      case 'liabilities':
        return {
          entries: getCategoryEntries('LIABILITY'),
          onSelect: p => beginCreate('LIABILITY', p),
        };
      default:
        return undefined;
    }
  };

  const renderSection = (category: (typeof categories)[number]) => (
    <FinancialSectionTable
      key={category.key}
      title={category.title}
      rows={category.rows}
      total={category.total}
      fullTotal={category.fullTotal}
      totalLabel={category.totalLabel}
      subSections={category.subSections}
      onRowClick={handleRowClick}
      addAction={addActionForCategory(category.key)}
      hideTotal={category.key === 'protections'}
      showOwnership={flagEnabledFinancialItemOwners}
    />
  );

  return (
    <Box>
      <Stack gap="lg">{balanceSheetCategories.map(renderSection)}</Stack>

      <Group justify="flex-end" gap={8} mt={24}>
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.5">
          Net worth:
        </Text>
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {formatMoney(netWorth, currency)}
        </Text>
      </Group>

      {protectionCategory && <Box mt={24}>{renderSection(protectionCategory)}</Box>}

      {modals}
    </Box>
  );
}
